"use client";
/**
 * Symbol store — selects the "currently charted symbol" for the
 * Price Chart pane and the handful of components that follow it
 * (Fundamentals, VPA, Seasonality, Screener, etc.).
 *
 * Originally a single global Zustand instance, which meant every
 * workspace shared one symbol — clicking a ticker in the Daily Brief
 * would also flip the Dashboard's chart. The store is now scope-aware:
 *
 *   - Components that don't care about isolation keep calling
 *     `useSymbolStore(selector)` exactly as before. With no provider
 *     they read/write the "global" store — identical to old behavior,
 *     including the localStorage key consumed by PriceChartPane.
 *
 *   - A workspace that wants its own isolated symbol wraps its subtree
 *     in `<SymbolScopeProvider value="daily-brief">`. Everything below
 *     it reads/writes a separate scope-keyed store and a separate
 *     localStorage key. Setting the symbol in Daily Brief can't bleed
 *     into the Dashboard's store, and vice versa.
 *
 * The scope name is also used as a suffix by PriceChartPane when it
 * persists the last-charted symbol — see SYMBOL_KEY there.
 */
import { createContext, useContext } from "react";
import { create, type StoreApi, type UseBoundStore } from "zustand";

type SymbolState = {
  symbol: string;
  setSymbol: (s: string) => void;
};

// Map of scope-name -> Zustand store. Stores are created lazily on
// first use and cached for the lifetime of the JS module so re-mounts
// of a workspace don't lose the active symbol.
const stores = new Map<string, UseBoundStore<StoreApi<SymbolState>>>();

function storeForScope(scope: string): UseBoundStore<StoreApi<SymbolState>> {
  let s = stores.get(scope);
  if (!s) {
    s = create<SymbolState>((set, get) => ({
      symbol: "AAPL",
      // Bail when the normalized symbol is unchanged so a redundant call can't
      // notify subscribers and drive a re-render loop (see SymbolSync).
      setSymbol: (sym) => {
        const next = sym.toUpperCase().replace(/\.US$/i, "");
        if (next && next !== get().symbol) set({ symbol: next });
      },
    }));
    stores.set(scope, s);
  }
  return s;
}

/** Imperatively read a scope's CURRENT symbol without subscribing. Returns
 *  null when the scope has no store yet (never set this session). The store Map
 *  is module-level, so the value SURVIVES a component unmount/remount — which
 *  lets a re-mounting view (e.g. a pane being maximized) initialize from the
 *  live symbol instead of a stale persisted default. */
export function peekScopeSymbol(scope: string): string | null {
  return stores.get(scope)?.getState().symbol ?? null;
}

/**
 * Seed a scope store from localStorage BEFORE the first paint.
 *
 * Live store wins when it already holds a non-default ticker (SPA remount).
 * Otherwise apply `fromStorage` so a hard reload doesn't flash/persist "AAPL"
 * over the user's last symbol. Call during render init — not in an effect —
 * so a mount-time save cannot poison LS with the virgin default.
 */
export function hydrateScopeSymbol(
  scope: string,
  fromStorage: string | null | undefined,
): string {
  const store = storeForScope(scope);
  const cur = store.getState().symbol;
  const saved = (fromStorage || "")
    .trim()
    .toUpperCase()
    .replace(/\.US$/i, "");
  if (cur && cur !== "AAPL") return cur;
  if (saved) {
    if (saved !== cur) store.getState().setSymbol(saved);
    return saved;
  }
  return cur;
}

/**
 * Force a scope's symbol, ignoring the "live store wins" rule in
 * `hydrateScopeSymbol`. Only for repairs: a locked board chart resetting itself
 * to its own ticker after something else overwrote it in this same session.
 */
export function setScopeSymbol(scope: string, symbol: string): void {
  const sym = (symbol || "").trim().toUpperCase().replace(/\.US$/i, "");
  if (!sym) return;
  storeForScope(scope).getState().setSymbol(sym);
}

const SymbolScopeContext = createContext<string>("global");

/** Wrap a subtree to give it its own isolated symbol store. The scope
 *  name is also the suffix used for the chart pane's localStorage key,
 *  so use something stable (e.g. "daily-brief", not a uuid). */
export const SymbolScopeProvider = SymbolScopeContext.Provider;

/** Read the scope name a component is rendering under. Useful for
 *  per-scope persistence keys. Returns "global" outside any provider. */
export function useSymbolScope(): string {
  return useContext(SymbolScopeContext);
}

/** Drop-in replacement for the previous Zustand hook. Picks the store
 *  for whatever scope the component is rendering under. */
export function useSymbolStore<T>(selector: (s: SymbolState) => T): T {
  const scope = useSymbolScope();
  const useStore = storeForScope(scope);
  return useStore(selector);
}
